import { createContext, useContext, useMemo, useReducer } from 'react';

import {
  EMBRYOS_PER_STRAW_VALUE,
  EMBRYOS_PER_VIT_STRAW_VALUE,
} from '../utils/constants';

export type FreezeFormState = {
  storageLocationId: string | null;
  /** Freeze site (emb.siteId on the backend) — derived from the selected storage location's siteId. */
  freezeLocation: string | null;
  freezeDay: string | null;
  predictionTechId: string | null;
  graderId: string | null;
  freezeTechId: string | null;
  freezer: string;
  embryosPerStraw: number | null;
  embryosPerVitStraw: number | null;
  dtPlus: boolean;
  outsideFreeze: boolean;
};

export type QCFormState = {
  egLotNumber: string;
  egExpDate: string;
  trypsinExpDate: string;
  ehmExpDate: string;
  sucroseExpDate: string;
  freezingComment: string;
  vsExpDate: string;
  esExpDate: string;
  strawLotNumber: string;
  vitStrawLotNumber: string;
  sterileStrawLotNumber: string;
  twelveWellLot: string;
};

export type StageKey = `s${4 | 5 | 6 | 7 | 8 | 9}`;
export type GradingData = Record<number, Record<StageKey, number | null>>;

type FormState = {
  batchForm: FreezeFormState;
  qcForm: QCFormState;
  gradingData: GradingData;
};

type FormAction =
  | { type: 'SET_BATCH'; field: keyof FreezeFormState; value: unknown }
  | { type: 'SET_BATCH_ALL'; form: Partial<FreezeFormState> }
  | { type: 'SET_QC'; field: keyof QCFormState; value: string }
  | { type: 'SET_QC_ALL'; form: Partial<QCFormState> }
  | { type: 'RESET_BATCH' }
  | { type: 'RESET_QC' }
  | { type: 'SET_GRADING'; grade: number; stage: number; value: number | null }
  | { type: 'SET_GRADING_ROWS'; rows: GradingData }
  | { type: 'CLEAR_GRADING' };

const initialBatchForm: FreezeFormState = {
  storageLocationId: null,
  freezeLocation: null,
  freezeDay: null,
  predictionTechId: null,
  graderId: null,
  freezeTechId: null,
  freezer: '',
  embryosPerStraw: EMBRYOS_PER_STRAW_VALUE,
  embryosPerVitStraw: EMBRYOS_PER_VIT_STRAW_VALUE,
  dtPlus: false,
  outsideFreeze: false,
};

const initialQCForm: QCFormState = {
  egLotNumber: '',
  egExpDate: '',
  trypsinExpDate: '',
  ehmExpDate: '',
  sucroseExpDate: '',
  freezingComment: '',
  vsExpDate: '',
  esExpDate: '',
  strawLotNumber: '',
  vitStrawLotNumber: '',
  sterileStrawLotNumber: '',
  twelveWellLot: '',
};

const STAGES = [4, 5, 6, 7, 8, 9] as const;
const GRADES = [1, 2, 3] as const;

const emptyGradingData = (): GradingData =>
  Object.fromEntries(
    GRADES.map((g) => [
      g,
      Object.fromEntries(STAGES.map((s) => [`s${s}`, null])) as Record<
        StageKey,
        number | null
      >,
    ]),
  ) as GradingData;

const initialState: FormState = {
  batchForm: initialBatchForm,
  qcForm: initialQCForm,
  gradingData: emptyGradingData(),
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_BATCH':
      return {
        ...state,
        batchForm: { ...state.batchForm, [action.field]: action.value },
      };
    case 'SET_BATCH_ALL':
      return {
        ...state,
        batchForm: { ...state.batchForm, ...action.form },
      };
    case 'SET_QC':
      return {
        ...state,
        qcForm: { ...state.qcForm, [action.field]: action.value },
      };
    case 'SET_QC_ALL':
      return {
        ...state,
        qcForm: { ...state.qcForm, ...action.form },
      };
    case 'RESET_BATCH':
      return { ...state, batchForm: initialBatchForm };
    case 'RESET_QC':
      return { ...state, qcForm: initialQCForm };
    case 'SET_GRADING':
      return {
        ...state,
        gradingData: {
          ...state.gradingData,
          [action.grade]: {
            ...state.gradingData[action.grade],
            [`s${action.stage}`]: action.value,
          },
        },
      };
    case 'SET_GRADING_ROWS':
      return { ...state, gradingData: action.rows };
    case 'CLEAR_GRADING':
      return { ...state, gradingData: emptyGradingData() };
    default:
      return state;
  }
}

type FreezeFormContextValue = FormState & {
  dispatch: React.Dispatch<FormAction>;
};

const FreezeFormContext = createContext<FreezeFormContextValue | null>(null);

export function FreezeFormProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

  return (
    <FreezeFormContext.Provider value={value}>
      {children}
    </FreezeFormContext.Provider>
  );
}

export function useFreezeForm() {
  const ctx = useContext(FreezeFormContext);
  if (!ctx) {
    throw new Error('useFreezeForm must be used within FreezeFormProvider');
  }
  return ctx;
}
